import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Save,
  AlertCircle,
  Sparkles,
  Scissors,
  History,
  FileText,
  Gauge,
  Scale,
  ClipboardCheck,
  ListChecks,
  Loader2,
  Pencil,
  Upload,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Maximize2,
  Minimize2,
  SlidersHorizontal,
  Wand2,
} from "lucide-react";
import { TiptapEditor } from "@/components/proposals/tiptap-editor";
import { ProposalSectionsSidebar } from "@/components/proposals/proposal-sections-sidebar";
import { ProposalStatsBar } from "@/components/proposals/proposal-stats-bar";
import {
  AutoSaveIndicator,
  type AutoSaveStatus,
} from "@/components/proposals/auto-save-indicator";
import { ExportMenu } from "@/components/proposals/export-menu";
import { AIGenerateDialog } from "@/components/proposals/ai-generate-dialog";
import { ResumeSectionDialog } from "@/components/proposals/resume-section-dialog";
import { SectionEvaluationPanel } from "@/components/proposals/section-evaluation-panel";
import { DiffPreviewModal } from "@/components/proposals/diff-preview-modal";
import { LegalVerificationDialog } from "@/components/proposals/legal-verification-dialog";
import { FormatPageDialog } from "@/components/proposals/format-page-dialog";
import { RequirementsCoverageDialog } from "@/components/proposals/requirements-coverage-dialog";
import { CriticHistoryPanel } from "@/components/proposals/critic-history-panel";
import { SectionRequirementsPanel } from "@/components/proposals/section-requirements-panel";
import { VersionHistory } from "@/components/proposals/version-history";
import {
  useProposal,
  useUpdateSection,
  useCreateSection,
  useReorderSections,
  useDeleteSection,
  useImportSectionFromFile,
  useEvaluateSection,
  useHumanizeSection,
  type CriticIteration,
  type Section,
} from "@/hooks/use-proposals";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { showToast } from "@/lib/toast";

const AUTOSAVE_DELAY = 1500;

/**
 * Normaliza HTML para comparar contenido semánticamente:
 * Tiptap convierte string vacío en "<p></p>" al inicializar el editor, lo que
 * dispararía un onUpdate fantasma. Tratamos ambos como equivalentes.
 */
function normalizeHtml(html: string): string {
  const trimmed = (html || "").trim();
  if (trimmed === "" || trimmed === "<p></p>" || trimmed === "<p><br></p>") {
    return "";
  }
  return trimmed;
}

/**
 * Encabezado de sección editable inline (mejora "Editabilidad del
 * índice"). Click en el lápiz → input; Enter/blur guarda, Escape cancela.
 */
function EditableHeading({
  title,
  onRename,
}: {
  title: string;
  onRename: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(title);
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [editing, title]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== title) onRename(trimmed);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
          }
        }}
        className="w-full text-2xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-blue-300 focus:outline-none"
        aria-label="Renombrar sección"
      />
    );
  }

  return (
    <div className="group/title flex items-center gap-2 mb-6 pb-2 border-b-2 border-gray-200">
      <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Renombrar sección"
        aria-label="Renombrar sección"
        className="p-1 text-gray-300 hover:text-gray-600 opacity-0 group-hover/title:opacity-100 transition-opacity"
      >
        <Pencil className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Página de edición de una propuesta.
 *
 * Actúa como orquestador delgado: maneja la selección de sección, el estado
 * local del editor (con debounce de auto-save) y compone los componentes
 * modulares de propuestas. Toda la lógica de red vive en hooks de React Query.
 */
export default function ProposalEditorPage() {
  const params = useParams<{ id: string; proposalId: string }>();
  const licitacionId = params.id!;
  const proposalId = params.proposalId!;
  const navigate = useNavigate();

  const { data: proposal, isLoading, error } = useProposal(proposalId);
  const updateSection = useUpdateSection(proposalId);
  const createSection = useCreateSection(proposalId);
  // Mejora "Editabilidad del índice": reordenar/eliminar secciones.
  const reorderSections = useReorderSections(proposalId);
  const deleteSection = useDeleteSection(proposalId);
  // Mejora "Importar desde Word/PDF".
  const importFromFile = useImportSectionFromFile(proposalId);
  const importInputRef = useRef<HTMLInputElement>(null);
  // Mejora M (changelog cliente): evaluación manual de sección.
  const evaluateSection = useEvaluateSection(proposalId);
  // Mejora "humanizar": reescritura de estilo con diff de aprobación.
  const humanizeSection = useHumanizeSection(proposalId);

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  // Sección pendiente de confirmación de borrado (null = dialog cerrado).
  const [sectionToDelete, setSectionToDelete] = useState<Section | null>(null);
  const [localContent, setLocalContent] = useState<Record<string, string>>({});
  const [pendingSections, setPendingSections] = useState<Set<string>>(new Set());
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>("saved");
  // Generación asíncrona: al COMPLETAR una sección forzamos el remonte del
  // editor (que va keyed por id) bumpeando esta versión, para que el contenido
  // recién generado sustituya al del editor aunque la sección ya esté en
  // `localContent`. Ref del estado de generación previo por sección → detectar
  // la transición GENERANDO→COMPLETADO/ERROR y avisar una sola vez.
  const [genVersion, setGenVersion] = useState<Record<string, number>>({});
  const prevGenStatusRef = useRef<Record<string, string | undefined>>({});
  // Sprint 4: cache local del último critic_iterations recibido por sección.
  // El estado local desaparece tras un refetch, así que también lo leemos
  // del backend (proposal.sections[].critic_iterations) como source of truth.
  const [localCritic, setLocalCritic] = useState<
    Record<string, { iterations: CriticIteration[]; finalScore: number | null }>
  >({});
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Refs espejo del último estado, para el flush en desmontaje/beforeunload
  // (bug 3.3): un cleanup no puede leer el estado "fresco" por closure.
  const pendingRef = useRef<Set<string>>(pendingSections);
  const localContentRef = useRef<Record<string, string>>(localContent);
  const updateSectionRef = useRef(updateSection);
  useEffect(() => {
    pendingRef.current = pendingSections;
  }, [pendingSections]);
  useEffect(() => {
    localContentRef.current = localContent;
  }, [localContent]);
  useEffect(() => {
    updateSectionRef.current = updateSection;
  }, [updateSection]);

  // Diálogos
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  // Mejora B (changelog cliente): diálogo de resumen a N palabras.
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  // Mejora "humanizar": HTML humanizado pendiente de revisión en el diff
  // (null = modal cerrado).
  const [humanizePreviewHtml, setHumanizePreviewHtml] = useState<string | null>(null);
  // Mejora N2 (changelog cliente): diálogo del verificador legal.
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const [coverageDialogOpen, setCoverageDialogOpen] = useState(false);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  // Sección madre cuando se crea una SUBSECCIÓN (null = sección raíz).
  const [newSectionParentId, setNewSectionParentId] = useState<string | null>(null);

  // Rediseño UX: layout de 3 columnas (índice | editor | inspector).
  // El índice y el inspector son colapsables y su estado se persiste.
  const [indexCollapsed, setIndexCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("proposalIndexCollapsed") === "1";
  });
  const [inspectorOpen, setInspectorOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("proposalInspectorOpen") !== "0";
  });
  const [inspectorTab, setInspectorTab] = useState<
    "requisitos" | "evaluacion" | "critico"
  >("requisitos");
  // Menú "Análisis" de la barra superior (Historial / Marco legal / Cobertura).
  const [analysisMenuOpen, setAnalysisMenuOpen] = useState(false);
  // Modo concentración: oculta índice + inspector + barra de stats.
  const [focusMode, setFocusMode] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "proposalIndexCollapsed",
        indexCollapsed ? "1" : "0"
      );
    } catch {
      /* localStorage no disponible */
    }
  }, [indexCollapsed]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "proposalInspectorOpen",
        inspectorOpen ? "1" : "0"
      );
    } catch {
      /* localStorage no disponible */
    }
  }, [inspectorOpen]);

  const sections = useMemo(() => proposal?.sections ?? [], [proposal]);

  // Seleccionar primera sección al cargar
  useEffect(() => {
    if (sections.length > 0 && !selectedSectionId) {
      const sorted = [...sections].sort((a, b) => a.section_order - b.section_order);
      setSelectedSectionId(sorted[0].id);
    }
  }, [sections, selectedSectionId]);

  // Sincronizar localContent con secciones del backend (sólo entradas no editadas)
  useEffect(() => {
    if (sections.length === 0) return;
    setLocalContent((prev) => {
      const next: Record<string, string> = { ...prev };
      let changed = false;
      for (const section of sections) {
        if (!(section.id in next)) {
          next[section.id] = section.content_html || "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sections]);

  // Generación asíncrona: detectar la transición de estado de cada sección
  // (el polling de `useProposal` refresca `sections` mientras algo genera).
  //   GENERANDO → COMPLETADO: el contenido ya está persistido en el backend y
  //     el editor lo lee directamente (no está en pending). Aquí solo limpiamos
  //     estado transitorio (pending + auto-saves en vuelo), sincronizamos
  //     localContent, remontamos el editor y avisamos.
  //   GENERANDO → ERROR: avisamos con el detalle; el contenido previo se queda.
  useEffect(() => {
    if (sections.length === 0) return;
    const prev = prevGenStatusRef.current;
    const nextStatuses: Record<string, string | undefined> = {};
    const justCompleted: string[] = [];
    let erroredMsg: { id: string; msg: string } | null = null;

    for (const section of sections) {
      const status = section.generation_status;
      nextStatuses[section.id] = status;
      const before = prev[section.id];
      if (before === "GENERANDO" && status === "COMPLETADO") {
        justCompleted.push(section.id);
      } else if (before === "GENERANDO" && status === "ERROR") {
        erroredMsg = {
          id: section.id,
          msg: section.generation_error || "La generación de la sección falló.",
        };
      }
    }

    if (justCompleted.length > 0) {
      const completedSet = new Set(justCompleted);
      const byId = new Map(sections.map((s) => [s.id, s]));
      setLocalContent((lc) => {
        const merged = { ...lc };
        for (const id of justCompleted) {
          merged[id] = byId.get(id)?.content_html ?? merged[id] ?? "";
        }
        return merged;
      });
      setPendingSections((ps) => {
        if (![...completedSet].some((id) => ps.has(id))) return ps;
        const nextSet = new Set(ps);
        for (const id of completedSet) nextSet.delete(id);
        return nextSet;
      });
      // Cancelar auto-saves en vuelo de las secciones recién generadas para que
      // no pisen el contenido nuevo con el viejo del editor.
      for (const id of justCompleted) {
        if (debounceTimers.current[id]) {
          clearTimeout(debounceTimers.current[id]);
          delete debounceTimers.current[id];
        }
      }
      setGenVersion((gv) => {
        const nv = { ...gv };
        for (const id of justCompleted) nv[id] = (nv[id] ?? 0) + 1;
        return nv;
      });
      showToast.success(
        justCompleted.length === 1
          ? "Sección generada con IA"
          : `${justCompleted.length} secciones generadas con IA`
      );
    }

    if (erroredMsg) {
      showToast.error(erroredMsg.msg);
    }

    prevGenStatusRef.current = nextStatuses;
  }, [sections]);

  // Bug 3.3: al desmontar (navegar) o recargar, NO perder los cambios dentro
  // de la ventana de auto-save. Flusheamos lo pendiente (fire-and-forget, la
  // mutación de React Query sobrevive al desmontaje) y avisamos con
  // `beforeunload` si el usuario intenta cerrar la pestaña con cambios sin
  // guardar.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingRef.current.size > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      Object.values(debounceTimers.current).forEach(clearTimeout);
      debounceTimers.current = {};
      for (const id of pendingRef.current) {
        const html = localContentRef.current[id];
        if (html !== undefined) {
          updateSectionRef.current.mutate({ sectionId: id, content_html: html });
        }
      }
    };
  }, []);

  const saveSection = useCallback(
    async (sectionId: string, html: string) => {
      try {
        setSaveStatus("saving");
        await updateSection.mutateAsync({ sectionId, content_html: html });
        setPendingSections((prev) => {
          const next = new Set(prev);
          next.delete(sectionId);
          // Bug 3.8: solo "Guardado" cuando NO queda ninguna otra sección
          // pendiente (antes marcaba "Guardado" al terminar UNA aunque
          // otras siguieran en cola).
          setSaveStatus(next.size === 0 ? "saved" : "unsaved");
          return next;
        });
      } catch {
        setSaveStatus("error");
        showToast.error("Error al guardar la sección");
      }
    },
    [updateSection]
  );

  const handleContentChange = useCallback(
    (sectionId: string, html: string) => {
      // Buscar el contenido original de la sección en el backend para comparar
      const originalSection = sections.find((s) => s.id === sectionId);

      // La sección se está generando por IA: su contenido lo escribe el backend
      // en segundo plano. Ignoramos cualquier onChange del editor (p.ej. el
      // onUpdate fantasma de Tiptap al montar/desmontar) para no programar un
      // auto-save que pise el contenido generado.
      if (originalSection?.generation_status === "GENERANDO") {
        return;
      }

      const originalHtml = normalizeHtml(originalSection?.content_html ?? "");
      const newHtml = normalizeHtml(html);

      // Si el contenido normalizado es idéntico al original, no marcar como
      // pending ni disparar save (evita auto-save fantasma cuando Tiptap
      // emite onUpdate al inicializar con contenido vacío).
      if (newHtml === originalHtml && !(sectionId in localContent)) {
        return;
      }

      setLocalContent((prev) => ({ ...prev, [sectionId]: html }));

      // Si el usuario revierte el contenido al original, limpiar el pending
      if (newHtml === originalHtml) {
        setPendingSections((prev) => {
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
        if (debounceTimers.current[sectionId]) {
          clearTimeout(debounceTimers.current[sectionId]);
          delete debounceTimers.current[sectionId];
        }
        setSaveStatus("saved");
        return;
      }

      setPendingSections((prev) => new Set(prev).add(sectionId));
      setSaveStatus("unsaved");

      if (debounceTimers.current[sectionId]) {
        clearTimeout(debounceTimers.current[sectionId]);
      }
      debounceTimers.current[sectionId] = setTimeout(() => {
        saveSection(sectionId, html);
        delete debounceTimers.current[sectionId];
      }, AUTOSAVE_DELAY);
    },
    [saveSection, sections, localContent]
  );

  const handleSaveAll = async () => {
    Object.values(debounceTimers.current).forEach(clearTimeout);
    debounceTimers.current = {};

    const ids = Array.from(pendingSections);
    if (ids.length === 0) {
      showToast.info("No hay cambios pendientes");
      return;
    }

    setSaveStatus("saving");
    try {
      await Promise.all(
        ids.map((id) =>
          updateSection.mutateAsync({ sectionId: id, content_html: localContent[id] || "" })
        )
      );
      setPendingSections(new Set());
      setSaveStatus("saved");
      showToast.success("Todos los cambios guardados");
    } catch {
      setSaveStatus("error");
      showToast.error("Error al guardar algunos cambios");
    }
  };

  const handleAIInsert = useCallback(
    (section: Section) => {
      if (!selectedSectionId) return;
      // Bug 3.5: cancelar cualquier auto-save pendiente de esta sección; si no,
      // el guardado tardío pisaría el contenido recién insertado por la IA.
      if (debounceTimers.current[selectedSectionId]) {
        clearTimeout(debounceTimers.current[selectedSectionId]);
        delete debounceTimers.current[selectedSectionId];
      }
      // El hook ya persistió el contenido en el backend, sólo sincronizamos el local.
      setLocalContent((prev) => ({ ...prev, [selectedSectionId]: section.content_html }));
      setPendingSections((prev) => {
        const next = new Set(prev);
        next.delete(selectedSectionId);
        return next;
      });
      // Sprint 4: cachear el historial del crítico para mostrarlo bajo el editor
      // antes de que el refetch traiga el dato actualizado del backend.
      if (section.critic_iterations && section.critic_iterations.length > 0) {
        setLocalCritic((prev) => ({
          ...prev,
          [selectedSectionId]: {
            iterations: section.critic_iterations!,
            finalScore: section.final_quality_score ?? null,
          },
        }));
      } else {
        // Generación con crítico desactivado: limpiar cualquier cache previa
        setLocalCritic((prev) => {
          const next = { ...prev };
          delete next[selectedSectionId];
          return next;
        });
      }
      setSaveStatus("saved");
    },
    [selectedSectionId]
  );

  const handleCreateSection = async () => {
    // Bug 3.9: un doble Enter creaba secciones duplicadas.
    if (createSection.isPending) return;
    const title = newSectionTitle.trim();
    if (!title) {
      showToast.error("El título no puede estar vacío");
      return;
    }
    try {
      // Si se abrió el diálogo desde el botón de una sección, la nueva entra
      // como SUBSECCIÓN suya; si no, como sección raíz.
      const parentId = newSectionParentId;
      const hermanos = parentId
        ? sections.filter((s) => s.parent_section_id === parentId).length
        : sections.filter((s) => !s.parent_section_id).length;

      const created = await createSection.mutateAsync({
        title,
        section_type: "CUSTOM",
        section_order: hermanos,
        content_html: "",
        parent_section_id: parentId,
      });
      setSelectedSectionId(created.id);
      setNewSectionTitle("");
      setNewSectionParentId(null);
      setAddSectionDialogOpen(false);
    } catch {
      // toast manejado por el hook
    }
  };

  /** Abre el diálogo para crear una subsección dentro de `parentId`. */
  const handleAddSubsection = useCallback((parentId: string) => {
    setNewSectionParentId(parentId);
    setNewSectionTitle("");
    setAddSectionDialogOpen(true);
  }, []);

  // --- Mejora "Editabilidad del índice": mover / renombrar / eliminar ---

  // Construye el nuevo orden global (lista plana de IDs) al mover una
  // sección entre sus hermanos. Mantiene cada subsección contigua a su
  // padre para no romper la jerarquía de 2 niveles.
  const handleReorder = useCallback(
    (sectionId: string, direction: -1 | 1) => {
      const sorted = [...sections].sort(
        (a, b) => a.section_order - b.section_order
      );
      const target = sorted.find((s) => s.id === sectionId);
      if (!target) return;

      // Hermanos = mismo parent_section_id (o raíces si no tiene padre).
      const parentId = target.parent_section_id ?? null;
      const siblings = sorted.filter(
        (s) => (s.parent_section_id ?? null) === parentId
      );
      const idx = siblings.findIndex((s) => s.id === sectionId);
      const swapWith = idx + direction;
      if (swapWith < 0 || swapWith >= siblings.length) return;

      // Reordenar el array de hermanos.
      const reordered = [...siblings];
      [reordered[idx], reordered[swapWith]] = [
        reordered[swapWith],
        reordered[idx],
      ];

      // Reconstruir la lista plana global: raíces en su orden, y bajo
      // cada raíz sus hijas en su orden. Si reordenamos raíces usamos el
      // nuevo orden de raíces; si reordenamos hijas, el de esas hijas.
      const rootsOrder =
        parentId === null
          ? reordered
          : sorted.filter((s) => !s.parent_section_id);
      const childrenByParent: Record<string, Section[]> = {};
      for (const s of sorted) {
        if (s.parent_section_id) {
          (childrenByParent[s.parent_section_id] ||= []).push(s);
        }
      }
      if (parentId !== null) {
        childrenByParent[parentId] = reordered;
      }

      const flatIds: string[] = [];
      for (const root of rootsOrder) {
        flatIds.push(root.id);
        for (const child of childrenByParent[root.id] ?? []) {
          flatIds.push(child.id);
        }
      }

      reorderSections.mutate({ section_ids: flatIds });
    },
    [sections, reorderSections]
  );

  const handleRename = useCallback(
    (sectionId: string, title: string) => {
      updateSection.mutate({ sectionId, title });
    },
    [updateSection]
  );

  // Importar contenido de la sección activa desde un .docx o .pdf. El
  // backend ya persiste; reflejamos el contenido importado en el editor
  // al instante.
  const handleImportFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // permite re-subir el mismo fichero
      if (!file || !selectedSectionId) return;
      const sectionId = selectedSectionId;
      importFromFile.mutate(
        { sectionId, file },
        {
          onSuccess: (updated) => {
            // Bug 3.5: cancelar el auto-save pendiente para que no pise el
            // contenido importado con una versión previa.
            if (debounceTimers.current[sectionId]) {
              clearTimeout(debounceTimers.current[sectionId]);
              delete debounceTimers.current[sectionId];
            }
            setLocalContent((prev) => ({
              ...prev,
              [sectionId]: updated.content_html,
            }));
            setPendingSections((prev) => {
              const next = new Set(prev);
              next.delete(sectionId);
              return next;
            });
          },
        }
      );
    },
    [selectedSectionId, importFromFile]
  );

  const handleConfirmDelete = useCallback(() => {
    if (!sectionToDelete) return;
    const deletedId = sectionToDelete.id;
    deleteSection.mutate(
      { sectionId: deletedId },
      {
        onSuccess: () => {
          // Bug 3.4: cancelar el timer pendiente (evita un PUT 404 sobre la
          // sección ya borrada) y purgar su id de todos los estados locales
          // (si no, quedaba para siempre en pendingSections y rompía
          // "Guardar ahora").
          if (debounceTimers.current[deletedId]) {
            clearTimeout(debounceTimers.current[deletedId]);
            delete debounceTimers.current[deletedId];
          }
          setPendingSections((prev) => {
            if (!prev.has(deletedId)) return prev;
            const next = new Set(prev);
            next.delete(deletedId);
            return next;
          });
          setLocalContent((prev) => {
            if (!(deletedId in prev)) return prev;
            const next = { ...prev };
            delete next[deletedId];
            return next;
          });
          setLocalCritic((prev) => {
            if (!(deletedId in prev)) return prev;
            const next = { ...prev };
            delete next[deletedId];
            return next;
          });
          // Si borramos la sección activa, seleccionar otra.
          if (selectedSectionId === deletedId) {
            const remaining = sections.filter((s) => s.id !== deletedId);
            setSelectedSectionId(remaining[0]?.id ?? null);
          }
          setSectionToDelete(null);
        },
        onError: () => setSectionToDelete(null),
      }
    );
  }, [sectionToDelete, deleteSection, selectedSectionId, sections]);

  // Sección actualmente seleccionada (objeto completo)
  const selectedSection = useMemo(
    () => sections.find((s) => s.id === selectedSectionId) ?? null,
    [sections, selectedSectionId]
  );

  // Sprint 4: resolver el historial del crítico para la sección activa.
  // Preferimos la cache local (devuelta por el hook tras generar) sobre el
  // backend (puede estar stale entre el commit y el refetch del editor).
  const criticForSelected = useMemo(() => {
    if (!selectedSection) return null;
    const local = localCritic[selectedSection.id];
    if (local && local.iterations.length > 0) {
      return { iterations: local.iterations, finalScore: local.finalScore ?? 0 };
    }
    if (selectedSection.critic_iterations && selectedSection.critic_iterations.length > 0) {
      return {
        iterations: selectedSection.critic_iterations,
        finalScore: selectedSection.final_quality_score ?? 0,
      };
    }
    return null;
  }, [selectedSection, localCritic]);

  // Estadísticas (usan localContent cuando está disponible)
  const stats = useMemo(() => {
    let totalWords = 0;
    let completedSections = 0;
    for (const section of sections) {
      const html = localContent[section.id] ?? section.content_html ?? "";
      const text = html.replace(/<[^>]*>/g, " ").trim();
      const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
      totalWords += words;
      if (words > 0) completedSections += 1;
    }
    return { totalWords, completedSections };
  }, [sections, localContent]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Cargando editor...</p>
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error al cargar</h2>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "Propuesta no encontrada"}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/licitaciones/${licitacionId}/propuestas`)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Volver a propuestas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Barra superior — acciones de NIVEL PROPUESTA */}
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => navigate(`/licitaciones/${licitacionId}/propuestas`)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            <span>Volver</span>
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <button
            type="button"
            onClick={() => {
              setIndexCollapsed((v) => !v);
              setFocusMode(false);
            }}
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md"
            title={indexCollapsed ? "Mostrar índice de secciones" : "Ocultar índice de secciones"}
            aria-label={indexCollapsed ? "Mostrar índice" : "Ocultar índice"}
          >
            {indexCollapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
          <h1 className="text-sm font-semibold text-gray-800 truncate">
            {proposal.title}
            <span className="ml-2 text-xs font-normal text-gray-500">
              v{proposal.version}
            </span>
          </h1>
        </div>

        <div className="flex items-center gap-1.5">
          <AutoSaveIndicator status={saveStatus} />

          <button
            type="button"
            onClick={handleSaveAll}
            disabled={updateSection.isPending}
            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            title="Guardar ahora (la propuesta también se guarda sola)"
            aria-label="Guardar"
          >
            <Save className="w-4 h-4" />
          </button>

          {/* Menú "Análisis" — herramientas de propuesta completa (uso ocasional) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setAnalysisMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Análisis</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {analysisMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setAnalysisMenuOpen(false)}
                />
                <div className="absolute right-0 mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCoverageDialogOpen(true);
                      setAnalysisMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                    title="Comprobar qué requisitos del pliego cubre la propuesta completa"
                  >
                    <ClipboardCheck className="w-4 h-4 text-gray-500" />
                    Cobertura del pliego
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLegalDialogOpen(true);
                      setAnalysisMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                    title="Detectar referencias legales desactualizadas en todas las secciones"
                  >
                    <Scale className="w-4 h-4 text-gray-500" />
                    Verificar marco legal
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormatDialogOpen(true);
                      setAnalysisMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                    title="Límites de formato del pliego y recuento de páginas de la propuesta"
                  >
                    <FileText className="w-4 h-4 text-gray-500" />
                    Formato y páginas
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHistoryOpen(true);
                      setAnalysisMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                  >
                    <History className="w-4 h-4 text-gray-500" />
                    Historial de versiones
                  </button>
                </div>
              </>
            )}
          </div>

          <ExportMenu proposalId={proposalId} />

          <div className="h-5 w-px bg-gray-300 mx-0.5" />
          <button
            type="button"
            onClick={() => setFocusMode((v) => !v)}
            className={`p-2 rounded-md hover:bg-gray-100 ${
              focusMode ? "text-purple-600" : "text-gray-500 hover:text-gray-800"
            }`}
            title={focusMode ? "Salir de modo concentración" : "Modo concentración (solo editor)"}
            aria-label="Modo concentración"
          >
            {focusMode ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setInspectorOpen((v) => !v);
              setFocusMode(false);
            }}
            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md"
            title={inspectorOpen ? "Ocultar panel de la sección" : "Mostrar panel de la sección"}
            aria-label="Panel de la sección"
          >
            {inspectorOpen ? (
              <PanelRightClose className="w-4 h-4" />
            ) : (
              <PanelRightOpen className="w-4 h-4" />
            )}
          </button>
        </div>
      </header>

      {/* Cuerpo — 3 columnas: índice | editor | inspector de la sección */}
      <div className="flex flex-1 overflow-hidden">
        {!focusMode && (
          <ProposalSectionsSidebar
            sections={sections}
            activeSection={selectedSectionId}
            pendingSections={pendingSections}
            onSelectSection={setSelectedSectionId}
            onAddSection={() => {
              setNewSectionParentId(null);
              setAddSectionDialogOpen(true);
            }}
            isAddingSection={createSection.isPending}
            onReorder={handleReorder}
            onRename={handleRename}
            onDelete={(section) => setSectionToDelete(section)}
            onAddSubsection={handleAddSubsection}
            collapsed={indexCollapsed}
            onToggleCollapse={() => setIndexCollapsed((v) => !v)}
          />
        )}

        {/* CENTRO — editor */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          {selectedSection ? (
            <>
              {/* Cabecera de sección + acciones de NIVEL SECCIÓN */}
              <div className="border-b border-gray-200 bg-white px-6 pt-4 pb-3">
                <EditableHeading
                  key={`title-${selectedSection.id}`}
                  title={selectedSection.title}
                  onRename={(t) => handleRename(selectedSection.id, t)}
                />
                <div
                  className="flex items-center gap-2 mt-2 flex-wrap"
                  role="group"
                  aria-label="Acciones de esta sección"
                >
                  <button
                    type="button"
                    onClick={() => setAiDialogOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Generar con IA</span>
                  </button>
                  {(() => {
                    const lastEval = selectedSection.last_manual_evaluation;
                    // El límite de calidad (88%) se quitó a petición del cliente:
                    // el usuario decide cuándo reevaluar. Solo mantenemos el
                    // mínimo de palabras para evaluar algo con sentido.
                    const tooShort = (selectedSection.word_count ?? 0) < 50;
                    const disabled = tooShort || evaluateSection.isPending;
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          setInspectorOpen(true);
                          setFocusMode(false);
                          setInspectorTab("evaluacion");
                          evaluateSection.mutate({ sectionId: selectedSection.id });
                        }}
                        disabled={disabled}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-300 text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          tooShort
                            ? "Necesitas al menos 50 palabras antes de evaluar"
                            : "Evaluar la calidad de esta sección contra el pliego"
                        }
                      >
                        {evaluateSection.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Gauge className="w-4 h-4" />
                        )}
                        <span>{lastEval ? "Reevaluar" : "Evaluar sección"}</span>
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => setResumeDialogOpen(true)}
                    disabled={(selectedSection.word_count ?? 0) < 50}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-300 text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      (selectedSection.word_count ?? 0) < 50
                        ? "Necesitas al menos 50 palabras antes de resumir"
                        : "Resumir a un número objetivo de palabras"
                    }
                  >
                    <Scissors className="w-4 h-4" />
                    <span>Resumir</span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      humanizeSection.mutate(
                        { sectionId: selectedSection.id },
                        {
                          onSuccess: (result) =>
                            setHumanizePreviewHtml(result.content_html),
                        }
                      )
                    }
                    disabled={
                      (selectedSection.word_count ?? 0) < 50 ||
                      humanizeSection.isPending
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-300 text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      (selectedSection.word_count ?? 0) < 50
                        ? "Necesitas al menos 50 palabras antes de humanizar"
                        : "Reescribir el estilo para que suene a redacción humana (verás los cambios antes de aplicarlos)"
                    }
                  >
                    {humanizeSection.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    <span>Humanizar</span>
                  </button>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                    className="hidden"
                    onChange={handleImportFile}
                  />
                  <button
                    type="button"
                    onClick={() => importInputRef.current?.click()}
                    disabled={importFromFile.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Importar el contenido de esta sección desde un Word (.docx) o un PDF (.pdf). El formato complejo (imágenes, estilos) puede no conservarse; en PDF se importa el texto como párrafos."
                  >
                    {importFromFile.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span>Importar Word/PDF</span>
                  </button>
                </div>
              </div>

              {/* Lienzo de redacción */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="max-w-3xl mx-auto">
                  {selectedSection.generation_status === "GENERANDO" ? (
                    // Generación asíncrona en curso: bloqueamos el editor para
                    // no editar contenido que la IA va a reemplazar. El
                    // contenido aparece solo al completar (polling).
                    <div
                      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-purple-300 bg-purple-50/50 py-16 text-center"
                      role="status"
                      aria-live="polite"
                    >
                      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                      <p className="text-sm font-medium text-purple-800">
                        Generando la sección con IA…
                      </p>
                      <p className="max-w-sm text-xs text-gray-500">
                        El escritor y el agente crítico están redactando
                        &ldquo;{selectedSection.title}&rdquo;. Puede tardar uno o
                        dos minutos; el contenido aparecerá aquí al terminar.
                      </p>
                    </div>
                  ) : (
                    <TiptapEditor
                      key={`${selectedSection.id}-${genVersion[selectedSection.id] ?? 0}`}
                      // `localContent` es un overlay SOLO para secciones con
                      // ediciones sin guardar (pending). Si no hay pending, el
                      // backend manda. Esto evita que el editor se monte con un
                      // `localContent` vacío/rancio (p.ej. "<p></p>" puesto al
                      // seleccionar la sección vacía) por ENCIMA del contenido
                      // recién generado por IA y lo pise con un auto-save fantasma.
                      content={
                        pendingSections.has(selectedSection.id)
                          ? localContent[selectedSection.id] ??
                            selectedSection.content_html ??
                            ""
                          : selectedSection.content_html ?? ""
                      }
                      onChange={(html) =>
                        handleContentChange(selectedSection.id, html)
                      }
                      placeholder={`Escribe el contenido de "${selectedSection.title}"...`}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p>Selecciona una sección para comenzar a editar</p>
              </div>
            </div>
          )}
        </main>

        {/* DERECHA — inspector de la SECCIÓN activa (pestañas) */}
        {selectedSection && inspectorOpen && !focusMode && (
          <aside className="w-[380px] flex-shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
            <div
              className="flex items-stretch border-b border-gray-200 bg-white"
              role="tablist"
              aria-label="Panel de la sección"
            >
              {(
                [
                  { id: "requisitos", label: "Requisitos", Icon: ListChecks },
                  { id: "evaluacion", label: "Evaluación", Icon: Gauge },
                  { id: "critico", label: "Crítico", Icon: Sparkles },
                ] as const
              ).map((t) => {
                const active = inspectorTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setInspectorTab(t.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                      active
                        ? "border-purple-600 text-purple-700"
                        : "border-transparent text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    <t.Icon className="w-3.5 h-3.5" />
                    {t.label}
                    {t.id === "evaluacion" &&
                      selectedSection.last_manual_evaluation && (
                        <span className="ml-0.5 px-1 rounded-full text-[10px] bg-gray-100 text-gray-600">
                          {Math.round(
                            selectedSection.last_manual_evaluation.score * 100
                          )}
                          %
                        </span>
                      )}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {inspectorTab === "requisitos" && (
                <SectionRequirementsPanel
                  proposalId={proposalId}
                  section={selectedSection}
                  sections={sections}
                />
              )}

              {inspectorTab === "evaluacion" &&
                (selectedSection.last_manual_evaluation ? (
                  <SectionEvaluationPanel
                    key={selectedSection.id}
                    proposalId={proposalId}
                    section={selectedSection}
                    evaluation={selectedSection.last_manual_evaluation}
                    stale={!!selectedSection.manual_evaluation_stale}
                    isReevaluating={evaluateSection.isPending}
                    onReevaluate={() =>
                      evaluateSection.mutate({ sectionId: selectedSection.id })
                    }
                    onApplyRefined={(refinedHtml) => {
                      handleContentChange(selectedSection.id, refinedHtml);
                      showToast.success(
                        "Sugerencias aplicadas. El contenido se guardará automáticamente."
                      );
                    }}
                  />
                ) : (
                  <div className="text-center text-sm text-gray-500 py-10 px-4">
                    <Gauge className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>Aún no has evaluado esta sección.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Pulsa <span className="font-medium">Evaluar sección</span> para
                      que el crítico la analice contra el pliego.
                    </p>
                  </div>
                ))}

              {inspectorTab === "critico" &&
                (criticForSelected ? (
                  <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                    <CriticHistoryPanel
                      iterations={criticForSelected.iterations}
                      finalScore={criticForSelected.finalScore}
                    />
                  </div>
                ) : (
                  <div className="text-center text-sm text-gray-500 py-10 px-4">
                    <Sparkles className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>Sin historial del crítico.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Aparece aquí cuando generas contenido con IA (loop
                      crítico-escritor).
                    </p>
                  </div>
                ))}
            </div>
          </aside>
        )}
      </div>

      {!focusMode && (
        <ProposalStatsBar
          wordCount={stats.totalWords}
          sectionsCompleted={stats.completedSections}
          sectionsTotal={sections.length}
          licitacionId={licitacionId}
        />
      )}

      {/* Diálogos */}
      {selectedSection && (
        <AIGenerateDialog
          isOpen={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          proposalId={proposalId}
          sectionId={selectedSection.id}
          sectionTitle={selectedSection.title}
        />
      )}

      {/* Mejora B (changelog cliente): diálogo de resumen a N palabras. */}
      {selectedSection && (
        <ResumeSectionDialog
          isOpen={resumeDialogOpen}
          onClose={() => setResumeDialogOpen(false)}
          proposalId={proposalId}
          sectionId={selectedSection.id}
          sectionTitle={selectedSection.title}
          currentWordCount={selectedSection.word_count ?? 0}
          onApply={handleAIInsert}
        />
      )}

      {/* Mejora "humanizar": diff de aprobación antes de aplicar el estilo. */}
      {selectedSection && humanizePreviewHtml !== null && (
        <DiffPreviewModal
          currentHtml={
            localContent[selectedSection.id] ?? selectedSection.content_html ?? ""
          }
          refinedHtml={humanizePreviewHtml}
          onReject={() => setHumanizePreviewHtml(null)}
          onAccept={() => {
            handleContentChange(selectedSection.id, humanizePreviewHtml);
            setHumanizePreviewHtml(null);
            showToast.success(
              "Estilo humanizado aplicado. El contenido se guardará automáticamente."
            );
          }}
        />
      )}

      <VersionHistory
        proposalId={proposalId}
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />

      {/* Mejora N2 (changelog cliente): diálogo del verificador legal. */}
      <LegalVerificationDialog
        isOpen={legalDialogOpen}
        onClose={() => setLegalDialogOpen(false)}
        proposalId={proposalId}
        proposalTitle={proposal.title}
      />

      {/* Feature "Límites de formato": límites del pliego + recuento de páginas. */}
      <FormatPageDialog
        isOpen={formatDialogOpen}
        onClose={() => setFormatDialogOpen(false)}
        licitacionId={licitacionId}
        proposalId={proposalId}
      />

      {/* Mejora "B": cobertura de requisitos del pliego. */}
      <RequirementsCoverageDialog
        isOpen={coverageDialogOpen}
        onClose={() => setCoverageDialogOpen(false)}
        proposalId={proposalId}
        proposalTitle={proposal.title}
      />

      {/* Diálogo de añadir sección */}
      {addSectionDialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setAddSectionDialogOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {newSectionParentId ? "Nueva subsección" : "Nueva sección"}
            </h3>
            {newSectionParentId && (
              <p className="text-sm text-gray-500 mb-3">
                Dentro de{" "}
                <strong>
                  {sections.find((s) => s.id === newSectionParentId)?.title ?? "la sección"}
                </strong>
              </p>
            )}
            <label
              htmlFor="new-section-title"
              className="block text-sm font-medium text-gray-700 mb-2 mt-3"
            >
              {newSectionParentId ? "Título de la subsección" : "Título de la sección"}
            </label>
            <input
              id="new-section-title"
              type="text"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateSection();
              }}
              placeholder="Ej: Anexos, Garantías, Equipo técnico..."
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setAddSectionDialogOpen(false);
                  setNewSectionTitle("");
                }}
                disabled={createSection.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateSection}
                disabled={createSection.isPending || !newSectionTitle.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createSection.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Crear sección
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de borrado de sección (mejora "Editabilidad del índice") */}
      <ConfirmDialog
        open={sectionToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteSection.isPending) setSectionToDelete(null);
        }}
        title="Eliminar sección"
        description={
          sectionToDelete
            ? `¿Eliminar la sección “${sectionToDelete.title}”?${
                sections.some(
                  (s) => s.parent_section_id === sectionToDelete.id
                )
                  ? " Se eliminarán también sus subsecciones."
                  : ""
              } Esta acción no se puede deshacer.`
            : ""
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="destructive"
        loading={deleteSection.isPending}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
